import { Button, Form } from 'react-bootstrap'
import { BsPauseFill, BsPlay, BsPlayFill } from 'react-icons/bs'
import React, {ChangeEventHandler, FC, useEffect, useRef, useState} from 'react'
import { EntityId } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'
import {
  AudioModel,
  fetchAudioById,
  selectAudioById,
  selectIsAudioIdInProgress,
} from '@store/slices/audio.slice'
import { AppDispatch, RootState } from '@store/store'
import {debounce, formatDjangoTimeString} from '../../tools/time.tools'
import {useTheme} from "@config/theme.config";
import {toast} from "react-toastify";

const AudioPlayButtonComponent: FC<{
  title: string | null
  audioId: EntityId | null
  playOnChange?: boolean
  setPlayOnChange?: (play: boolean) => void
  showSlider?: boolean
  showSpeedSelect?: boolean
}> = ({ title, audioId, playOnChange = false, setPlayOnChange , showSlider = true, showSpeedSelect = true}) => {
  const dispatch = useDispatch<AppDispatch>()

  const isAudioLoading = useSelector((state: RootState) =>
    audioId ? selectIsAudioIdInProgress(state, audioId) : null
  )

  const audio: AudioModel | null = useSelector((state: RootState) =>
    audioId ? selectAudioById(state, audioId) : null
  )

  useEffect(() => {
    if (audioId && !audio && !isAudioLoading) {
      dispatch(fetchAudioById(audioId))
    }
  }, [dispatch, audioId])

  return (
    <>
      {audio ? (
        <AudioPlayButton
          audio={audio}
          title={title}
          playOnChange={playOnChange}
          setPlayOnChange={setPlayOnChange}
          showSlider={showSlider}
          showSpeedSelect={showSpeedSelect}
        ></AudioPlayButton>
      ) : (
        <AudioNotFoundButton></AudioNotFoundButton>
      )}
    </>
  )
}

const AudioNotFoundButton: FC = () => (
  <Button
    title={'Zvuková stopa chybí'}
    variant={'outline-success'}
    disabled={true}
    className="d-flex align-items-center text-light"
    style={{ width: '40px', height: '40px' }}
  >
    <BsPlayFill />
  </Button>
)

const AudioPlayButton: FC<{
  title: string | null
  audio: AudioModel
  playOnChange: boolean
  setPlayOnChange?: (play: boolean) => void
  showSlider: boolean
  showSpeedSelect: boolean
}> = ({ title, audio, playOnChange, setPlayOnChange , showSlider, showSpeedSelect}) => {
  const { theme } = useTheme()
  const [isAudioPlaying, setIsAudioPlaying] = useState<boolean>(false)
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const audioPlay = useRef<HTMLAudioElement | null>(null)
  const initTimeout = useRef<number | null>(null)

  useEffect(() => {
    if (initTimeout.current) clearTimeout(initTimeout.current)

    initTimeout.current = window.setTimeout(() => {
      audioPlay.current = new Audio(audio.publicPath)
      audioPlay.current.addEventListener('ended', () => {
        setIsAudioPlaying(false)
      })
      audioPlay.current.addEventListener('timeupdate', handleAudioTimeUpdate);
      if (playOnChange) toggleAudioPlaying()
    }, 1000)

    // Cleanup function
    return () => {
      if (initTimeout.current) {
        clearTimeout(initTimeout.current)
        initTimeout.current = null
      }
      if (audioPlay.current) {
        audioPlay.current.removeEventListener('ended', () => {
          setIsAudioPlaying(false)
        })
        audioPlay.current.removeEventListener('timeupdate', handleAudioTimeUpdate);
        audioPlay.current = null
      }
    }
  }, [audio.publicPath])


  const handleAudioError = () => {
    toast.error('Toto audio je nedostupné, zkuste náslech přegenerovat.')
    setIsAudioPlaying(false)
  }

  const playAudio = () => {
    if (!audioPlay.current) return
    audioPlay.current.play()
    // audioPlay.current.play().catch(handleAudioError)
    setIsAudioPlaying(true)
  }

  const pauseAudio = () => {
    if (!audioPlay.current) return
    audioPlay.current.pause()
    setIsAudioPlaying(false)
    if (setPlayOnChange) setPlayOnChange(false)
  }

  const toggleAudioPlaying = () => {
    if (isAudioPlaying) {
      pauseAudio()
    } else {
      playAudio()
    }
  }

  const debouncedToggleAudioPlaying = debounce(toggleAudioPlaying)

  const handleSeekChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(event.target.value);
    setCurrentTime(time);
    if (audioPlay.current) {
      audioPlay.current.currentTime = time;
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioPlay.current) {
      setCurrentTime(audioPlay.current.currentTime);
    }
  };

  const handlePlaybackSpeedChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const speed = parseFloat(event.target.value);
    setPlaybackSpeed(speed);
    if (audioPlay.current) {
      audioPlay.current.playbackRate = speed;
    }
  };


  return (
      <div className={`text-${theme == 'light' ? 'dark' : 'light'} d-flex flex-row w-100`}>
        <Button
            title={title || ''}
            variant="success"
            onClick={debouncedToggleAudioPlaying}
            style={{ width: '40px', height: '40px' }}
        >
          {isAudioPlaying ? <BsPauseFill/> : <BsPlayFill/>}
        </Button>
        <div className="d-flex flex-row w-100 flex-grow-1">
          { showSpeedSelect &&
          <Form.Group controlId="playbackSpeed" className="d-flex flex-row">
            <Form.Control
                as="select"
                value={playbackSpeed}
                onChange={handlePlaybackSpeedChange}
                style={{marginRight: "5px", marginLeft: "5px", width: "58px"}}>
              <option value={0.8}>0.8x</option>
              <option value={1}>1x</option>
              <option value={1.2}>1.2x</option>
              <option value={1.5}>1.5x</option>
              <option value={2}>2x</option>
            </Form.Control>
          </Form.Group>
          }
          {showSlider &&
            <input
                type="range"
                min={0}
                max={formatDjangoTimeString(audio.length) || 0}
                step={0.01}
                value={currentTime}
                onChange={handleSeekChange}
                onMouseDown={pauseAudio}
                onMouseUp={playAudio}
                onInput={handleAudioTimeUpdate}
                className="w-100 ml-2 mr-2"
            />
          }
        </div>
      </div>

  )
}

export default AudioPlayButtonComponent
